import { Action, Reducer } from 'redux';
import { AppThunkAction } from '.';
import { addDays, addMinutes,  setHours, setMinutes, addYears } from 'date-fns';
import Notify from '../components/common/Notify';
import { isNullOrUndefined } from 'util';
import * as Login from './Login';

/**
 * This ts file handles all API calls to/from ModelPointController.cs
 * It also includes an API call to SubmissionController.cs for creating changes
 * */

// -----------------
// STATE - This defines the type of data maintained in the Redux store.

export interface DataPointState {
    currentData: DataPoint[];
    pendingData: DataPoint[];
    currentFilterLists: [];
    pendingFilterLists: [];
    pendingDataLoading: boolean;
    currentDataLoading: boolean;
    pendingDataLoaded: boolean;
    currentDataLoaded: boolean;
    currentRefreshDate: Date;
    pendingRefreshDate: Date;
}

export interface DataPoint {
    id: string;
    detailId: string;
    submissionType: string;
    dataItem: string;
    dataItemOld: string;
    deviceId: string;
    deviceType: string;
    interconnect: string;
    remoteName: string;
    areaEms: string;
    emsStation: string;
    type: string;
    qualityStatus: string;
    kv: string;
    tsStatus: string;
    unitMeas: string;
    periodicity: string;
    remoteAddOrRemove: [];
    isModeled: boolean;
    isComplete: boolean;
    usses: boolean;
    scadaPlaceholder: boolean;
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.

interface RequestDataAction {
    type: 'DATAPOINTS_REQUEST_DATA';
    dataType: string;
}

interface ReceiveDataAction {
    type: 'DATAPOINTS_RECEIVE_DATA';
    dataPoints: DataPoint[];
    filterLists: [];
    dataType: string;
    refreshDate: Date;
}

interface RefreshDataAction {
    type: 'DATAPOINTS_REFRESH_DATA';
    dataType: string;
}

interface CreateChangeAction {
    type: 'DATAPOINTS_CREATE_CHANGE';
}

interface UpdateDataAction {
    type: 'DATAPOINTS_UPDATE_DATA';
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction = RequestDataAction | ReceiveDataAction | CreateChangeAction | RefreshDataAction | UpdateDataAction;

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).

// All of the below fetch API calls go to the ModelPointController.cs file
export const actionCreators = {
    requestData: (dataType: string, interconnect: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        let user = Login.actionCreators.getUser();
        const appState = getState();

        if (appState && appState.dataPoints && ((dataType === 'pending' && !appState.dataPoints.pendingDataLoaded && !appState.dataPoints.pendingDataLoading) ||
            (dataType === 'current' && !appState.dataPoints.currentDataLoaded && !appState.dataPoints.currentDataLoading))) {
            fetch(`api/data/` + dataType, {
                method: 'POST',
                body: JSON.stringify({
                    UserName: user.userName,
                    Interconnect: interconnect
                }),
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json"
                }
            })
                .then(response => {
                    if (response.status === 200) {
                        (response.json())// as Promise<DataPoint[]>)
                            .then(data => {
                                // declare a new date of the current date/time
                                let refreshDate = new Date();
                                // set refreshDate to be next day at 1:20 AM if data type is Current
                                if (dataType === 'current') {
                                    // Add a day to the refreshDate
                                    refreshDate = addDays(refreshDate, 1);
                                    // set the hour of refreshDate to 1 AM
                                    refreshDate = setHours(refreshDate, 1);
                                    // set the minutes of refreshDate to 20, so final time is next day @ 1:20 AM
                                    refreshDate = setMinutes(refreshDate, 20);
                                }
                                else {
                                    // Check if the user has set the refresh timer via their user profile or not
                                    // if not, leave it as default (2 minutes)
                                    if (localStorage.getItem('refreshTimer') === null)
                                        refreshDate = addMinutes(refreshDate, 2);
                                    // Otherwise check that the user hasn't disabled the refresh timer. If not, set refreshDate to the number of minutes the user has specified in their User Profile
                                    else if (localStorage.getItem('refreshTimer') !== 'never')
                                        refreshDate = addMinutes(refreshDate, parseInt(localStorage.getItem('refreshTimer') as string))
                                }
                                dispatch({ type: 'DATAPOINTS_RECEIVE_DATA', dataPoints: data.dtos, filterLists: data.filterLists, dataType, refreshDate });
                                if (data.dtos.length === 0)
                                    Notify({ severity: 'info', message: 'No data available.' });
                            });
                    }
                    else {
                        (response.json())
                            .then(data => {
                                dispatch({ type: 'DATAPOINTS_REQUEST_DATA', dataType });
                                Notify({ severity: 'error', message: data.message });
                            })
                    }
                })
            dispatch({ type: 'DATAPOINTS_REQUEST_DATA', dataType: dataType });
            Notify({ severity: 'info', message: 'Loading data' });
        }
    },
    refreshData: (dataType: string, forceRefresh?: boolean): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (actionCreators.getToken('dataRefresh') !== "") {
            forceRefresh = true;
            localStorage.removeItem('dataRefresh');
        }

        if (appState && appState.dataPoints && forceRefresh) {
            Notify({ severity: 'info', message: 'Refreshing data' });
            dispatch({ type: 'DATAPOINTS_REFRESH_DATA', dataType });
        }
    },
    updateData: (dtos: [], finalize: any): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (appState && appState.submissions && dtos !== undefined) {
            if (dtos.length > 0) {
                fetch(`api/data/update`, {
                    method: 'POST',
                    body: JSON.stringify(dtos),
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json"
                    }
                })
                    .then(response => {
                        if (response.status === 200) {
                            //success! reload changes. notify user
                            (response.json())
                                .then(data => {
                                    dispatch({ type: 'DATAPOINTS_UPDATE_DATA' });
                                    Notify({ severity: 'success', message: data.message })
                                    finalize();
                                });
                        }
                        else {
                            //failure. notify user.
                            (response.json())
                                .then(data => {
                                    Notify({ severity: 'error', message: data.message })
                                });
                        }
                    })
            }
            else
                Notify({ severity: 'info', message: 'No updates found for this submission.' })
        }
    },
    createChange: (dtos: [], clearSelection: any): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();
        if (appState && appState.dataPoints && !isNullOrUndefined(dtos)) {
            fetch(`api/change/create`,
                {
                    method: 'POST',
                    body: JSON.stringify(dtos),
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json; charset=utf-8"
                    }
                })
                .then(response => {
                    if (response.status === 200) {
                        (response.json())
                            .then(data => {
                                dispatch({ type: 'DATAPOINTS_CREATE_CHANGE' });
                                Notify({ severity: 'success', message: data.message })
                                localStorage.setItem('reloadData', JSON.stringify('true'));
                                clearSelection();
                            });
                    }
                    else {
                        (response.json())
                            .then(data => {
                                dispatch({ type: 'DATAPOINTS_CREATE_CHANGE'});
                                Notify({ severity: 'error', message: data.message })
                            });
                    }
                })
        }
    },
    // Checks if the refreshTimer has been changed by the user from their user profile. 
    // Determine best way to update this. I think we really only care if the user disabled their refresh and then re-enabled it or vice versa?
    checkRefreshTimer: () => {

    },
    // retrieves token from localStorage
    getToken: (id: string) => {

        let token = localStorage.getItem(id) || "";

        if (token !== "" && typeof token !== 'undefined' && token !== null)
            return JSON.parse(token);
        else
            return "";
    },
        
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.

const unloadedState: DataPointState = { pendingDataLoading: false, pendingDataLoaded: false, currentDataLoading: false, currentDataLoaded: false, pendingData: [], currentData: [], currentFilterLists: [], pendingFilterLists: [], currentRefreshDate: addDays(new Date(), 1), pendingRefreshDate: new Date() };

export const reducer: Reducer<DataPointState> = (state: DataPointState | undefined, incomingAction: Action): DataPointState => {
    if (state === undefined) {
        return unloadedState;
    }

    const action = incomingAction as KnownAction;
    switch (action.type) {
        case 'DATAPOINTS_REQUEST_DATA':
            if (action.dataType === 'pending') {
                return {
                    ...state,
                    pendingDataLoading: true,
                    pendingDataLoaded: false
                };
            }
            else {
                return {
                    ...state,
                    currentDataLoading: true,
                    currentDataLoaded: false
                };
            }
        case 'DATAPOINTS_RECEIVE_DATA':
            if (action.dataType === 'pending') {
                return {
                    ...state,
                    pendingRefreshDate: action.refreshDate,
                    pendingData: action.dataPoints,
                    pendingFilterLists: action.filterLists,
                    pendingDataLoading: false,
                    pendingDataLoaded: true
                };
            }
            else{
                return {
                    ...state,
                    // only updating refreshDate for 'current' data since this is source data
                    // that is refreshed nightly by ICCPST DataLoader
                    currentRefreshDate: action.refreshDate,
                    currentData: action.dataPoints,
                    currentFilterLists: action.filterLists,
                    currentDataLoading: false,
                    currentDataLoaded: true
                };
            }
        case 'DATAPOINTS_UPDATE_DATA':
            return {
                ...state,
                pendingDataLoading: false,
                pendingDataLoaded: false,
                pendingData: [],
                pendingFilterLists: []
            };
        case 'DATAPOINTS_REFRESH_DATA':
            if (action.dataType === 'pending') {
                return {
                    ...state,
                    pendingDataLoading: false,
                    pendingDataLoaded: false,
                    pendingData: [],
                    pendingFilterLists: []
                };
            }
            else {
                return {
                    ...state,
                    currentDataLoading: false,
                    currentDataLoaded: false,
                    currentData: [],
                    currentFilterLists: []
                };
            }
        case 'DATAPOINTS_CREATE_CHANGE':
            return {
                ...state
            };
    }
    return state;
};